<style scoped>
    .label { font-weight: 600; }
    .doc-row {
        border-bottom: 1px solid #e9ecef;
        padding-bottom: 1rem;
        margin-bottom: 1rem;
    }
    .doc-row:last-of-type { border-bottom: none; }
</style>

<template>
    <div class="">
        <app-overlay-loader v-if="preloader" />
        <div class="card-body" style="margin-top: -4%;">
            <form ref="form" :class="{ 'loading-opacity': preloader }">

                <!-- Documentos ya subidos -->
                <div class="form-group card-with-shadow p-3 mt-3">
                    <h6 class="mb-3">{{ $t('uploaded_documents') }}</h6>

                    <p v-if="!savedDocuments.length" class="text-muted mb-0">
                        {{ $t('no_documents') }}
                    </p>

                    <div v-else class="table-responsive">
                        <table class="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>{{ $t('document_type') }}</th>
                                    <th>{{ $t('file') }}</th>
                                    <th>{{ $t('received_date') }}</th>
                                    <th>{{ $t('size') }}</th>
                                    <th class="text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="d in savedDocuments" :key="d.id">
                                    <td>{{ d.type }}</td>
                                    <td>{{ d.original_name }}</td>
                                    <td>{{ d.received_at || '-' }}</td>
                                    <td>{{ formatSize(d.size) }}</td>
                                    <td class="text-right text-nowrap">
                                        <a :href="downloadUrl(d)" class="btn btn-sm btn-outline-primary mr-1">
                                            {{ $t('download') }}
                                        </a>
                                        <button type="button" class="btn btn-sm btn-danger" @click="deleteDocument(d)">
                                            {{ $t('delete') }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Subir nuevos -->
                <div class="form-group card-with-shadow p-3 mt-3">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0">{{ $t('documents') }}</h6>
                        <button type="button" class="btn btn-sm btn-primary" @click="addDocument">
                            <app-icon name="plus" class="mr-1" />{{ $t('add') }}
                        </button>
                    </div>

                    <div v-for="(doc, index) in documents" :key="doc.key" class="row doc-row align-items-end">
                        <div class="col-md-4">
                            <div class="form-group mb-0">
                                <label class="label">{{ $t('document_type') }}</label>
                                <select class="form-control" v-model="doc.type">
                                    <option value="">{{ $t('select') }}</option>
                                    <option v-for="type in documentTypes" :key="type" :value="type">{{ type }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="col-md-5">
                            <div class="form-group mb-0">
                                <label class="label">{{ $t('file') }}</label>
                                <div class="custom-file">
                                    <input
                                        type="file"
                                        class="custom-file-input"
                                        :id="'doc-file-' + doc.key"
                                        @change="onFileChange($event, index)"
                                    />
                                    <label class="custom-file-label" :for="'doc-file-' + doc.key">
                                        {{ doc.file ? doc.file.name : $t('choose_file') }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-2">
                            <div class="form-group mb-0">
                                <label class="label">{{ $t('received_date') }}</label>
                                <input type="date" class="form-control" v-model="doc.received_at" />
                            </div>
                        </div>

                        <div class="col-md-1 text-right">
                            <button
                                type="button"
                                class="btn btn-sm btn-danger"
                                :disabled="documents.length === 1"
                                @click="removeDocument(index)"
                            >
                                <app-icon name="minus" />
                            </button>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="mt-4 col-md-3 action-buttons">
                        <button type="button" class="btn btn-primary w-100" @click="save">
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>

            </form>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { FormMixin } from '../../../../../core/mixins/form/FormMixin.js';

export default {
    name: "sub_contract_documents",
    mixins: [FormMixin],

    props: {
        props: { type: Object, default: () => ({}) }
    },

    data() {
        return {
            preloader: false,
            uid: 0,
            contractId: null,
            documents: [],
            savedDocuments: [],
            documentTypes: [
                'Invoice', 'BOL', 'Packing List', 'Certificate of Origin',
                'Certificate of Analysis', 'NOPIC', 'Load Report',
                '10+2 (ISF)', 'Arrival Notice', 'POD',
            ],
        };
    },

    created() {
        this.addDocument();
    },

    mounted() {
        this.contractId = this.props?.id ?? this.$route?.params?.id ?? null;
        if (!this.contractId) {
            console.warn('subcontract id not found', this.props, this.$route?.params);
            return;
        }
        this.loadDocuments();
    },

    methods: {
        addDocument() {
            this.documents.push({ key: this.uid++, type: '', file: null, received_at: '' });
        },

        removeDocument(index) {
            this.documents.splice(index, 1);
        },

        onFileChange(event, index) {
            const file = event.target.files[0] || null;
            this.$set(this.documents, index, { ...this.documents[index], file });
        },

        resetForm() {
            this.documents = [];
            this.addDocument();
        },

        errorMessage(e) {
            const errors = e.response?.data?.errors;
            return errors
                ? Object.values(errors).flat().join('\n')
                : (e.response?.data?.message || this.$t('something_went_wrong'));
        },

        async loadDocuments() {
            this.preloader = true;
            try {
                const { data } = await axios.get(`/api/subcontract/get_documents/${this.contractId}`);
                this.savedDocuments = Array.isArray(data) ? data : (data?.data ?? []);
            } catch (e) {
                this.$toastr.e(this.errorMessage(e));
            } finally {
                this.preloader = false;
            }
        },

        async save() {
            if (!this.contractId) {
                this.$toastr.e(this.$t('something_went_wrong'));
                return;
            }

            const docs = this.documents.filter(d => d.file);
            if (!docs.length) {
                this.$toastr.e(this.$t('choose_file'));
                return;
            }
            if (docs.some(d => !d.type)) {
                this.$toastr.e(this.$t('document_type'));
                return;
            }

            const form = new FormData();
            docs.forEach((d, i) => {
                form.append(`documents[${i}][type]`, d.type);
                form.append(`documents[${i}][file]`, d.file);
                if (d.received_at) form.append(`documents[${i}][received_at]`, d.received_at);
            });

            this.preloader = true;
            try {
                const { data } = await axios.post(`/api/subcontract/save_documents/${this.contractId}`, form);
                this.$toastr.s(data.message || this.$t('saved_successfully'));
                this.resetForm();
                await this.loadDocuments();
            } catch (e) {
                this.$toastr.e(this.errorMessage(e));
                this.preloader = false;
            }
        },

        downloadUrl(doc) {
            return `/api/subcontract/documents/${this.contractId}/${doc.id}/download`;
        },

        async deleteDocument(doc) {
            if (!window.confirm(`${this.$t('delete')} "${doc.original_name}"?`)) return;

            this.preloader = true;
            try {
                const { data } = await axios.delete(`/api/subcontract/documents/${this.contractId}/${doc.id}`);
                this.$toastr.s(data.message || this.$t('deleted_successfully'));
                await this.loadDocuments();
            } catch (e) {
                this.$toastr.e(this.errorMessage(e));
                this.preloader = false;
            }
        },

        formatSize(bytes) {
            if (!bytes) return '-';
            const units = ['B', 'KB', 'MB', 'GB'];
            const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
            return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
        },
    },
};
</script>